/**
 * KatalogBarRow - Compact header row that appears inside the navbar
 * when user scrolls past the hero on /account/katalog
 * 
 * Desktop: avatar + name (left), centered tabs (center), action icons (right)
 * Mobile: avatar + name (left), section dropdown (center), action icons (right)
 * 
 * Uses useKatalogBarStateHeader hook to read shared state from YourKatalogPage
 */
import React, { useState, useRef, useEffect } from 'react';
import { cn, Button, Popover, PopoverTrigger, PopoverContent, useIsMobile } from '@bright-web-skeleton/ui';
import { Search, SlidersHorizontal, LayoutGrid, List, X, ChevronDown, Check } from 'lucide-react';
import { useKatalogBarStateHeader, type KatalogTabValue, type ViewMode } from '../../hooks/useKatalogBarState';

export interface KatalogBarRowProps {
  avatarUrl?: string;
  name?: string;
  className?: string;
}

const tabs: { value: KatalogTabValue; label: string; disabled?: boolean }[] = [
  { value: 'katalog', label: 'KATALOG' },
  { value: 'events', label: 'EVENTS', disabled: true },
  { value: 'portfolio', label: 'PORTFOLIO' },
];

export const KatalogBarRow: React.FC<KatalogBarRowProps> = ({
  avatarUrl: propAvatarUrl,
  name: propName,
  className,
}) => {
  const isMobile = useIsMobile();
  // Get shared state from page
  const { 
    state, 
    setActiveTab, 
    setSearchQuery, 
    setViewMode, 
    toggleFilter 
  } = useKatalogBarStateHeader();
  
  // Fallback to props if state not yet available
  const name = state?.name || propName || 'Your Katalag';
  const avatarUrl = state?.avatarUrl || propAvatarUrl;
  const activeTab = state?.activeTab || 'katalog';
  const searchQuery = state?.searchQuery || '';
  const viewMode = state?.viewMode || 'grid';
  const filterActive = state?.filterActive || false;
  
  const safeInitial = name?.charAt(0)?.toUpperCase() || '?';
  
  // Local search state for popover
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Sync local search with state
  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);
  
  // Focus input when popover opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);
  
  const handleClearSearch = () => {
    setLocalSearch('');
    setSearchQuery('');
  };

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'KATALOG';

  return (
    <div 
      data-katalog-sticky-row
      className={cn("h-14 w-full flex", className)}
    >
      {/* Reserve sidebar width on desktop so content aligns with main */}
      <div className="hidden md:block md:w-64 shrink-0" />
      
      {/* Content area - 3-column grid for proper centering */}
      <div className="min-w-0 flex-1 relative">
        <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-6 h-full">
          {/* === DESKTOP LAYOUT === */}
          <div className="hidden md:grid md:grid-cols-3 h-full items-center">
            {/* Left: Avatar + Name */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-muted overflow-hidden shrink-0 ring-2 ring-background">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted text-xs font-bold text-muted-foreground">
                    {safeInitial}
                  </div>
                )}
              </div>
              <span className="font-semibold text-foreground text-sm truncate max-w-[140px]">
                {name}
              </span>
            </div>

            {/* Center: Tabs (truly centered) */}
            <div className="flex items-center justify-center gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => !tab.disabled && setActiveTab(tab.value)}
                  disabled={tab.disabled}
                  className={cn(
                    "px-4 py-1.5 rounded-full text-xs font-semibold transition-colors",
                    activeTab === tab.value
                      ? "bg-foreground text-background"
                      : tab.disabled
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Right: View Mode only — Search/Filter handled by KatalogStickybar */}
            <div className="flex items-center justify-end gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 rounded-full"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? (
                  <List className="h-4 w-4" />
                ) : (
                  <LayoutGrid className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* === MOBILE LAYOUT === */}
          <div className="flex md:hidden h-full items-center justify-between gap-2">
            {/* Left: Avatar + Name */}
            <div className="flex items-center gap-2 min-w-0 shrink">
              <div className="w-7 h-7 rounded-full bg-muted overflow-hidden shrink-0 ring-2 ring-background">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted text-[10px] font-bold text-muted-foreground">
                    {safeInitial}
                  </div>
                )}
              </div>
              <span className="font-semibold text-foreground text-xs truncate max-w-[80px]">
                {name}
              </span>
            </div>

            {/* Center: Section Dropdown */}
            <Popover open={sectionOpen} onOpenChange={setSectionOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground text-background text-xs font-semibold">
                  {activeTabLabel}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-40 p-1"
                sideOffset={8}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => {
                      if (!tab.disabled) {
                        setActiveTab(tab.value);
                        setSectionOpen(false);
                      }
                    }}
                    disabled={tab.disabled}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors",
                      activeTab === tab.value
                        ? "bg-muted font-medium"
                        : tab.disabled
                        ? "text-muted-foreground/40 cursor-not-allowed"
                        : "hover:bg-muted"
                    )}
                  >
                    {tab.label}
                    {activeTab === tab.value && (
                      <Check className="h-4 w-4 text-foreground" />
                    )}
                  </button>
                ))}
              </PopoverContent>
            </Popover>

            {/* Right: Action icons */}
            <div className="flex items-center gap-0.5 shrink-0">
              {/* Search Popover */}
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={cn(
                      "h-8 w-8 rounded-full",
                      (searchQuery || searchOpen) && "bg-muted"
                    )}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  align="end" 
                  className="w-[calc(100vw-2rem)] max-w-xs p-2"
                  sideOffset={8}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={localSearch}
                        onChange={(e) => {
                          setLocalSearch(e.target.value);
                          setSearchQuery(e.target.value);
                        }}
                        placeholder="Search deals..."
                        className="w-full h-10 pl-9 pr-8 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      {localSearch && (
                        <button
                          onClick={handleClearSearch}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                        >
                          <X className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Filter Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn(
                  "h-8 w-8 rounded-full",
                  filterActive && "bg-muted"
                )}
                onClick={toggleFilter}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>

              {/* View Mode Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? (
                  <List className="h-4 w-4" />
                ) : (
                  <LayoutGrid className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {/* DEBUG CANARY - REQUIRED FOR VERIFICATION */}
        <span className="absolute top-1 right-2 text-[9px] text-primary font-mono opacity-75 pointer-events-none">
          STICKYBAR_V2
        </span>
      </div>
    </div>
  );
};

export default KatalogBarRow;
